import React, { useEffect, useState } from "react";
import { inquiriesApi } from "../../lib/api";
import { Mail, Phone, Trash2, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";

const TYPE_LABEL = {
  contact: "Contact",
  buy: "Wij kopen",
  sell: "Verkoop",
  financing: "Financiering",
  car: "Auto-aanvraag",
};

export default function AdminInquiries() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    inquiriesApi.list().then(setItems).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleRead = async (it) => {
    await inquiriesApi.update(it.id, { read: !it.read });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Aanvraag verwijderen?")) return;
    await inquiriesApi.remove(id);
    toast({ title: "Verwijderd" });
    load();
  };

  const filtered =
    filter === "all" ? items : filter === "unread" ? items.filter((i) => !i.read) : items.filter((i) => i.type === filter);

  return (
    <div className="p-8">
      <Toaster />
      <div className="mb-7">
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
          Aanvragen <span className="text-[#c5a76f]">beheer</span>
        </h1>
        <p className="text-white/50 text-sm mt-1">{items.length} totale aanvragen · {items.filter((i) => !i.read).length} ongelezen</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          ["all", "Alle"],
          ["unread", "Ongelezen"],
          ["contact", "Contact"],
          ["buy", "Wij kopen"],
          ["sell", "Verkoop"],
          ["financing", "Financiering"],
          ["car", "Auto-aanvraag"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-4 py-1.5 text-sm rounded transition-colors ${
              filter === k ? "bg-[#c5a76f] text-black" : "bg-[#242424] text-white/70 hover:bg-white/10"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50">Laden...</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => (
            <div key={it.id} className={`bg-[#242424] border ${it.read ? "border-white/5" : "border-[#c5a76f]/40"} rounded-md overflow-hidden`}>
              <button onClick={() => setExpanded(expanded === it.id ? null : it.id)} className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-white/[0.02]">
                {!it.read && <span className="w-2 h-2 rounded-full bg-[#c5a76f] flex-shrink-0" />}
                <span className="px-2 py-0.5 bg-white/10 text-white/70 text-xs rounded uppercase tracking-wide">{TYPE_LABEL[it.type] || it.type}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{it.name}</div>
                  <div className="text-white/50 text-sm truncate">{it.subject || it.message?.slice(0, 80) || it.email}</div>
                </div>
                <div className="text-white/40 text-xs hidden md:block">{new Date(it.created_at).toLocaleDateString("nl-BE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                {expanded === it.id ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>
              {expanded === it.id && (
                <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-white/80">
                      <Mail className="w-4 h-4 text-[#c5a76f]" />
                      <a href={`mailto:${it.email}`} className="hover:text-[#c5a76f]">{it.email}</a>
                    </div>
                    {it.phone && (
                      <div className="flex items-center gap-2 text-white/80">
                        <Phone className="w-4 h-4 text-[#c5a76f]" />
                        <a href={`tel:${it.phone}`} className="hover:text-[#c5a76f]">{it.phone}</a>
                      </div>
                    )}
                  </div>
                  {it.message && (
                    <div className="bg-[#1a1a1a] border border-white/5 rounded p-4 text-white/80 text-sm whitespace-pre-wrap">{it.message}</div>
                  )}
                  {it.extra && Object.keys(it.extra).length > 0 && (
                    <div className="text-sm">
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Extra</div>
                      <ul className="space-y-1">
                        {Object.entries(it.extra).map(([k, v]) => (
                          v ? (
                            <li key={k} className="text-white/70">
                              <span className="text-white/50">{k}:</span> {String(v)}
                            </li>
                          ) : null
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex items-center gap-3 pt-2">
                    <button onClick={() => toggleRead(it)} className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-[#c5a76f] border border-white/10 rounded">
                      {it.read ? <><EyeOff className="w-3.5 h-3.5" /> Markeer ongelezen</> : <><Eye className="w-3.5 h-3.5" /> Markeer gelezen</>}
                    </button>
                    <button onClick={() => handleDelete(it.id)} className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:text-red-400 border border-white/10 rounded">
                      <Trash2 className="w-3.5 h-3.5" /> Verwijderen
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-10 text-white/40">Geen aanvragen gevonden.</div>}
        </div>
      )}
    </div>
  );
}
